import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Snackbar, Alert } from '@mui/joy';
import { duration } from '@mui/material';

type ToastSeverity = 'primary' | 'success' | 'neutral' | 'danger' | 'warning';

interface ToastOptions {
  message: string;
  severity?: ToastSeverity;
  duration?: number; // Duration in milliseconds
}

interface ToastContextType {
  showToast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toast, setToast] = useState<ToastOptions | null>(null);
  const [open, setOpen] = useState(false);

  const showToast = ({ message, severity = 'primary', duration = 5000 }: ToastOptions) => {
    setToast({ message, severity, duration });
    setOpen(true);
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toast && (
        <Snackbar
          autoHideDuration={toast.duration}
          onClose={() => setOpen(false)}
          color={toast.severity}
          variant='soft'
          open={open}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {toast.message}
        </Snackbar>
      )}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
